/**
 * Azure Storage resource identity (spec §3.1, ruling F1): the typed reference
 * that replaces every `database = service` trick. Shared renderer <-> main;
 * process-agnostic (no Node / DOM / SDK imports).
 *
 * Re-exported through `./azureStorage` (the pinned import path).
 */

export type StorageService = 'blob' | 'table' | 'queue' | 'file';

/** Per-service endpoint overrides; shared by `Connection.azureStorageEndpoints` and `serviceEndpointFor`. */
export type AzureStorageEndpoints = { blob?: string; table?: string; queue?: string; file?: string; dfs?: string };

/** Where the connection is rooted. `resource` attachments (SAS URL to one container / share / table / queue / HNS directory) never list siblings. */
export type StorageAttachmentScope =
  | { kind: 'account' }
  | { kind: 'resource'; resource: StorageResourceRef };

export type StorageResourceRef =
  | { kind: 'account' }
  | { kind: 'service'; service: StorageService }
  // Blob service
  | { kind: 'container'; container: string }
  | { kind: 'blobPrefix'; container: string; prefix: string }        // '' = container root; otherwise ends with '/'
  | { kind: 'blob'; container: string; name: string; snapshot?: string; versionId?: string }
  // Table service
  | { kind: 'table'; table: string }
  | { kind: 'entity'; table: string; partitionKey: string; rowKey: string }
  // Queue service
  | { kind: 'queue'; queue: string }
  | { kind: 'message'; queue: string; messageId: string; popReceipt?: string }
  // File service
  | { kind: 'share'; share: string; snapshot?: string }
  | { kind: 'directory'; share: string; path: string; snapshot?: string }   // path without leading '/', '' = share root
  | { kind: 'file'; share: string; path: string; snapshot?: string }
  // ADLS Gen2 (HNS) — same container, DFS endpoint semantics
  | { kind: 'hnsPath'; fileSystem: string; path: string; isDirectory: boolean };

/** The service a ref belongs to; 'account' answers undefined, 'service' answers its own service. */
export function storageServiceOf(ref: StorageResourceRef): StorageService | undefined {
  switch (ref.kind) {
    case 'account':
      return undefined;
    case 'service':
      return ref.service;
    case 'container':
    case 'blobPrefix':
    case 'blob':
    case 'hnsPath':
      return 'blob';
    case 'table':
    case 'entity':
      return 'table';
    case 'queue':
    case 'message':
      return 'queue';
    case 'share':
    case 'directory':
    case 'file':
      return 'file';
  }
}

/**
 * Stable, collision-free identity string. A JSON tuple (the `pinRef.ts`
 * precedent): `JSON.stringify` quotes and escapes every element, so blob names
 * containing '/', '|', ':' or '"' can never make two distinct refs collide the
 * way a delimiter join would. Used for TanStack Query keys, cursor binding,
 * node ids, tab dedupe and pin keys. `blob` and `hnsPath` views of one object
 * key differently on purpose (different endpoint, different semantics).
 */
export function storageResourceKey(ref: StorageResourceRef): string {
  switch (ref.kind) {
    case 'account':
      return JSON.stringify(['account']);
    case 'service':
      return JSON.stringify(['service', ref.service]);
    case 'container':
      return JSON.stringify(['container', ref.container]);
    case 'blobPrefix':
      return JSON.stringify(['blobPrefix', ref.container, ref.prefix]);
    case 'blob':
      return JSON.stringify(['blob', ref.container, ref.name, ref.snapshot ?? null, ref.versionId ?? null]);
    case 'table':
      return JSON.stringify(['table', ref.table]);
    case 'entity':
      return JSON.stringify(['entity', ref.table, ref.partitionKey, ref.rowKey]);
    case 'queue':
      return JSON.stringify(['queue', ref.queue]);
    case 'message':
      return JSON.stringify(['message', ref.queue, ref.messageId]);
    case 'share':
      return JSON.stringify(['share', ref.share, ref.snapshot ?? null]);
    case 'directory':
      return JSON.stringify(['directory', ref.share, ref.path, ref.snapshot ?? null]);
    case 'file':
      return JSON.stringify(['file', ref.share, ref.path, ref.snapshot ?? null]);
    case 'hnsPath':
      return JSON.stringify(['hnsPath', ref.fileSystem, ref.path, ref.isDirectory]);
  }
}

/**
 * The owning container / table / queue / share name, or 'account' for account
 * and service refs. The third element of every storage TanStack Query key
 * (`['azure-storage', connectionId, containerScopeKey(ref), storageResourceKey(ref), fingerprint]`)
 * so a mutation through either the Blob or the HNS view of one container can
 * invalidate both with one prefix (MASTER "Contracts to pin before S8").
 */
export function containerScopeKey(ref: StorageResourceRef): string {
  switch (ref.kind) {
    case 'account':
    case 'service':
      return 'account';
    case 'container':
    case 'blobPrefix':
    case 'blob':
      return ref.container;
    case 'hnsPath':
      return ref.fileSystem;
    case 'table':
    case 'entity':
      return ref.table;
    case 'queue':
    case 'message':
      return ref.queue;
    case 'share':
    case 'directory':
    case 'file':
      return ref.share;
  }
}

/** Filesystem-safe MetaInfo scope for Table storage declarations (spec §8.3). Explicit constant, not a service id. */
export const STORAGE_TABLE_METAINFO_SCOPE = 'storage-tables' as const;

/**
 * The MetaInfo `schema` slot for Table storage tables. Table storage has no
 * schemas, but the slot must be NON-EMPTY: the Table Explorer grid's loose-FK
 * matcher returns early on an empty schema, which would make loose-FK
 * declarations inert for entities. Ruling P2 (b), 2026-09-20 (spec §18) puts
 * loose-FK declarations + PartitionKey/RowKey lookup navigation in scope of
 * S4, so the pin is this constant, not '' (the SQLite precedent).
 */
export const STORAGE_TABLE_METAINFO_SCHEMA = 'storage' as const;

/**
 * The ONLY producer of the MetaInfo `{ database, schema, table }` slot for
 * Table storage entities (spec §6.4 / §8.3). Re-pinned 2026-09-20 by ruling
 * P2 (b): `schema` is `STORAGE_TABLE_METAINFO_SCHEMA`, never ''. No MetaInfo
 * migration is owed — S1 is unreleased, so no file was ever written under ''.
 */
export function storageMetaInfoScope(
  ref: Extract<StorageResourceRef, { kind: 'table' | 'entity' }>
): { database: typeof STORAGE_TABLE_METAINFO_SCOPE; schema: typeof STORAGE_TABLE_METAINFO_SCHEMA; table: string } {
  return { database: STORAGE_TABLE_METAINFO_SCOPE, schema: STORAGE_TABLE_METAINFO_SCHEMA, table: ref.table };
}
